/**
 * useHighlightPainter — React hook that (re-)paints highlight spans in the DOM
 * whenever the `highlights` array changes.
 *
 * Uses setHighlights() from @arianna/reader-render/highlightDom, which:
 *   1. Removes all existing .hl spans (restoring plain text)
 *   2. Applies each highlight via Range API → <span class="hl hl-{color}">
 *
 * The hook uses a layout effect (useLayoutEffect) so the paint happens
 * synchronously after DOM mutations but before the browser paints, avoiding
 * a flash of un-highlighted text on section switches.
 *
 * Consumed by:
 *   - apps/platform Reader.tsx (web)
 *   - apps/desktop (future)
 */

import { useLayoutEffect } from 'react'
import { setHighlights } from '@arianna/reader-render'
import type { ReaderRenderHighlight } from '@arianna/reader-render'

export interface HighlightPainterOptions {
  /**
   * The container element that holds the rendered [data-block-id] elements.
   * Pass a ref.current. When null, the hook is a no-op.
   */
  container: HTMLElement | null
  /**
   * The current set of highlights to render. Changing this triggers a
   * full repaint (clear + re-apply).
   */
  highlights: ReadonlyArray<ReaderRenderHighlight>
  /**
   * Dependency that indicates the DOM content has been replaced (e.g. section
   * index). When this changes, the hook re-paints even if `highlights` is
   * reference-equal, because the DOM nodes that the Range API needs are new.
   */
  sectionKey: string | number | null | undefined
}

/**
 * Paints / re-paints highlight spans synchronously after every DOM update.
 * The repaint runs when `highlights`, `container`, or `sectionKey` changes.
 */
export function useHighlightPainter({
  container,
  highlights,
  sectionKey,
}: HighlightPainterOptions): void {
  useLayoutEffect(() => {
    if (!container) return
    // setHighlights clears all existing .hl spans first, then re-applies.
    // This is safe to run on every render because it is idempotent.
    setHighlights(highlights)

    return () => {
      // On unmount (section change) clear spans so the next section starts
      // clean before React commits the new DOM.
      // Note: the next render's useLayoutEffect will call setHighlights again
      // with the correct highlights for the new section.
      // We do NOT call clearHighlights() here because the DOM nodes are about
      // to be replaced by React anyway — but it keeps things tidy in tests.
    }
  }, [container, highlights, sectionKey])
}
